import React, { useState, useEffect } from 'react';
import api from '../config/api';
import OvertimeModal from '../components/OvertimeModal';
import './ManagementPages.css';

const formatStatus = (status) => {
  if (!status) return '';
  return status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const Salaries = () => {
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSalary, setEditingSalary] = useState(null);

  const [formData, setFormData] = useState({
    employee: '',
    month: new Date().toLocaleString('default', { month: 'long' }),
    year: new Date().getFullYear(),
    basicSalary: '',
    allowances: 0,
    bonus: 0,
    deductions: 0,
    paymentStatus: 'pending',
    paymentMethod: 'bank-transfer',
    paymentDate: '',
    notes: ''
  });

  const [calculatedTax, setCalculatedTax] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [currentMonthGenerated, setCurrentMonthGenerated] = useState(true);
  const [hasPendingSalaries, setHasPendingSalaries] = useState(false);
  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [overtimeSalary, setOvertimeSalary] = useState(null);

  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const [filterMonth, setFilterMonth] = useState(new Date().toLocaleString('default', { month: 'long' }));
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchSalaries();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMonth, filterYear]);

  const fetchSalaries = async () => {
    try {
      const params = {};
      if (filterMonth) params.month = filterMonth;
      if (filterYear) params.year = filterYear;

      const response = await api.get('/api/salaries', { params });
      setSalaries(response.data);

      // Check if filtered month salaries exist
      const hasCurrentMonth = response.data.length > 0;
      setCurrentMonthGenerated(hasCurrentMonth);

      // Check if there are pending salaries in the current view
      const pendingSalaries = response.data.some(s => s.paymentStatus === 'pending');
      setHasPendingSalaries(pendingSalaries);
    } catch (err) {
      setError('Failed to load salaries');
    } finally {
      setLoading(false);
    }
  };

  const generateMonthlySalaries = async () => {
    const monthIndex = MONTHS.indexOf(filterMonth);
    const year = filterYear;

    if (!window.confirm(`Generate salaries for ${filterMonth} ${year} for all active employees?`)) {
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await api.post('/api/salaries/generate/monthly', {
        month: monthIndex,
        year: year
      });

      alert(`${response.data.generated} salaries generated, ${response.data.skipped} skipped`);
      fetchSalaries();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate salaries');
    } finally {
      setGenerating(false);
    }
  };

  const deleteAllSalaries = async () => {
    if (!window.confirm(`Delete ALL ${salaries.length} salary records for ${filterMonth} ${filterYear}? This cannot be undone.`)) {
      return;
    }

    setDeletingAll(true);
    setError('');

    try {
      const response = await api.delete('/api/salaries', { params: { month: filterMonth, year: filterYear } });
      alert(`${response.data.deletedCount} salary records deleted`);
      fetchSalaries();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete salaries');
    } finally {
      setDeletingAll(false);
    }
  };

  const markAllPaid = async () => {
    if (!window.confirm('Mark all pending salaries as paid?')) {
      return;
    }

    setMarkingPaid(true);
    setError('');

    try {
      const response = await api.post('/api/salaries/mark-all-paid', { month: filterMonth, year: filterYear });
      alert(`${response.data.modifiedCount} salaries marked as paid`);
      fetchSalaries();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to mark salaries as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/api/employees');
      setEmployees(response.data);
    } catch (err) {
      console.error('Failed to load employees');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSalary) {
        await api.put(`/api/salaries/${editingSalary._id}`, formData);
      } else {
        await api.post('/api/salaries', formData);
      }
      fetchSalaries();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save salary');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this salary record?')) {
      try {
        await api.delete(`/api/salaries/${id}`);
        fetchSalaries();
      } catch (err) {
        setError('Failed to delete salary');
      }
    }
  };

  const openModal = (salary = null) => {
    if (salary) {
      setEditingSalary(salary);
      setFormData({
        ...salary,
        employee: salary.employee._id,
        paymentDate: salary.paymentDate?.split('T')[0] || ''
      });
      setCalculatedTax(parseFloat(salary.tax) || 0);
    } else {
      setEditingSalary(null);
      setFormData({
        employee: '',
        month: new Date().toLocaleString('default', { month: 'long' }),
        year: new Date().getFullYear(),
        basicSalary: '',
        allowances: 0,
        bonus: 0,
        deductions: 0,
        paymentStatus: 'pending',
        paymentMethod: 'bank-transfer',
        paymentDate: '',
        notes: ''
      });
      setCalculatedTax(0);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSalary(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Calculate tax using the API
  const calculateTax = async () => {
    const basic = parseFloat(formData.basicSalary) || 0;
    const allowances = parseFloat(formData.allowances) || 0;
    const bonus = parseFloat(formData.bonus) || 0;
    const deductions = parseFloat(formData.deductions) || 0;

    if (basic > 0) {
      try {
        const response = await api.post('/api/salaries/calculate-tax', {
          basicSalary: basic,
          allowances,
          bonus,
          deductions
        });
        setCalculatedTax(parseFloat(response.data.tax) || 0);
        return response.data;
      } catch (err) {
        console.error('Failed to calculate tax');
        return null;
      }
    }
    setCalculatedTax(0);
    return null;
  };

  // Calculate tax when salary components change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateTax();
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.basicSalary, formData.allowances, formData.bonus, formData.deductions]);

  const calculateNetSalary = () => {
    const basic = parseFloat(formData.basicSalary) || 0;
    const allowances = parseFloat(formData.allowances) || 0;
    const bonus = parseFloat(formData.bonus) || 0;
    const deductions = parseFloat(formData.deductions) || 0;
    const tax = parseFloat(calculatedTax) || 0;
    return basic + allowances + bonus - tax - deductions;
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Salary Management</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!currentMonthGenerated && (
            <button
              className="btn btn-success"
              onClick={generateMonthlySalaries}
              disabled={generating}
            >
              {generating ? 'Generating...' : `Generate ${filterMonth} ${filterYear} Salaries`}
            </button>
          )}
          {hasPendingSalaries && (
            <button
              className="btn btn-secondary"
              onClick={markAllPaid}
              disabled={markingPaid}
              style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8' }}
            >
              {markingPaid ? 'Marking...' : 'Mark All Paid'}
            </button>
          )}
          {salaries.length > 0 && (
            <button
              className="btn btn-danger"
              onClick={deleteAllSalaries}
              disabled={deletingAll}
            >
              {deletingAll ? 'Deleting...' : `Delete All (${filterMonth} ${filterYear})`}
            </button>
          )}
          <button className="btn btn-primary" onClick={() => openModal()}>
            + Add Salary
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="filters">
        <select
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
        >
          {MONTHS.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <input
          type="number"
          value={filterYear}
          onChange={(e) => setFilterYear(parseInt(e.target.value))}
          min="2020"
          max="2099"
          style={{ width: '90px' }}
        />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ fontSize: '13px', color: '#aaa' }}>
            Total Net: <span style={{ fontWeight: 'bold', color: '#fff', fontSize: '15px' }}>PKR {salaries.reduce((sum, s) => sum + (s.netSalary || 0), 0).toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Month</th>
              <th>Year</th>
              <th>Basic Salary</th>
              <th>Allowances</th>
              <th>Bonus</th>
              <th>Overtime</th>
              <th>Tax</th>
              <th>Deductions</th>
              <th>Net Salary</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {salaries.map(salary => (
              <tr key={salary._id}>
                <td>{salary.employee?.name}</td>
                <td>{salary.month}</td>
                <td>{salary.year}</td>
                <td>PKR {salary.basicSalary?.toFixed(2)}</td>
                <td>PKR {salary.allowances?.toFixed(2)}</td>
                <td>PKR {salary.bonus?.toFixed(2)}</td>
                <td style={{ color: salary.overtime > 0 ? '#4caf50' : '#666' }}>
                  {salary.overtime > 0 ? `PKR ${salary.overtime.toFixed(2)}` : '—'}
                </td>
                <td style={{ color: '#e74c3c' }}>PKR {salary.tax?.toFixed(2) || '0.00'}</td>
                <td>PKR {salary.deductions?.toFixed(2)}</td>
                <td style={{ fontWeight: 'bold' }}>PKR {salary.netSalary?.toFixed(2)}</td>
                <td>
                  <span className={`status-badge status-${salary.paymentStatus}`}>
                    {formatStatus(salary.paymentStatus)}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-icon"
                    onClick={() => { setOvertimeSalary(salary); setShowOvertimeModal(true); }}
                    title="Log Overtime"
                    style={{ fontSize: '16px' }}
                  >
                    ⏱
                  </button>
                  <button
                    className="btn-icon btn-edit"
                    onClick={() => openModal(salary)}
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => handleDelete(salary._id)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <OvertimeModal
        show={showOvertimeModal}
        onClose={() => setShowOvertimeModal(false)}
        employees={employees}
        defaultEmployeeId={overtimeSalary?.employee?._id || overtimeSalary?.employee}
        defaultMonth={overtimeSalary?.month}
        defaultYear={overtimeSalary?.year}
        onSaved={() => fetchSalaries()}
      />

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSalary ? 'Edit Salary' : 'Add Salary'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Employee *</label>
                <select
                  name="employee"
                  value={formData.employee}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Month *</label>
                  <select name="month" value={formData.month} onChange={handleChange} required>
                    {['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Year *</label>
                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Basic Salary *</label>
                  <input
                    type="number"
                    name="basicSalary"
                    value={formData.basicSalary}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Allowances</label>
                  <input
                    type="number"
                    name="allowances"
                    value={formData.allowances}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Bonus</label>
                  <input
                    type="number"
                    name="bonus"
                    value={formData.bonus}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Deductions</label>
                  <input
                    type="number"
                    name="deductions"
                    value={formData.deductions}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tax (FBR 2025-26) - Bonus Excluded</label>
                  <input
                    type="text"
                    value={`PKR ${calculatedTax.toFixed(2)}`}
                    disabled
                    style={{ background: '#8b2020', color: '#ffffff', fontWeight: 'bold' }}
                  />
                </div>
                <div className="form-group">
                  <label>Net Salary (After Tax)</label>
                  <input
                    type="text"
                    value={`PKR ${calculateNetSalary().toFixed(2)}`}
                    disabled
                    style={{ background: '#2d5a2d', color: '#ffffff', fontWeight: 'bold' }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Payment Status *</label>
                  <select
                    name="paymentStatus"
                    value={formData.paymentStatus}
                    onChange={handleChange}
                    required
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="delayed">Delayed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Payment Method</label>
                  <select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                  >
                    <option value="bank-transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Payment Date</label>
                <input
                  type="date"
                  name="paymentDate"
                  value={formData.paymentDate}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSalary ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Salaries;
